import React from 'react';
import { Link } from 'react-router-dom';
import { Zap, Shield, BarChart3, Globe, CheckCircle, ArrowRight } from 'lucide-react';
import './Landing.css';

const Landing: React.FC = () => {
  const features = [
    {
      icon: Zap,
      title: 'Lightning Fast',
      description: 'Optimized for 60s streaming with adaptive chunking. Get complete responses without timeouts.'
    },
    {
      icon: Shield,
      title: 'Enterprise Security',
      description: 'IP locking, rate limiting, and JWT authentication to keep your API keys secure.'
    },
    {
      icon: BarChart3,
      title: 'Advanced Analytics',
      description: 'Track usage, monitor tokens, and analyze performance in real-time.'
    },
    {
      icon: Globe,
      title: '30+ AI Models',
      description: 'Access GPT-5, Claude 4.5, Gemini 2.5, Grok-4, DeepSeek R1, and more through one API.'
    }
  ];

  const plans = [
    {
      name: 'FREE',
      price: '$0',
      period: '/month',
      features: [
        '10 requests per minute',
        '1 IP address',
        '16,384 tokens per request',
        '5 AI models access',
        'Basic support'
      ],
      cta: 'Get Started',
      popular: false
    },
    {
      name: 'PRO',
      price: '$30',
      period: '/month',
      features: [
        '100 requests per minute',
        '3 IP addresses',
        '32,768 tokens per request',
        'All AI models',
        'Priority support',
        'Advanced analytics'
      ],
      cta: 'Start Free Trial',
      popular: true
    },
    {
      name: 'ENTERPRISE',
      price: '$200',
      period: '/month',
      features: [
        '6,000 requests per minute',
        '30 IP addresses',
        '65,536 tokens per request',
        'All AI models',
        '24/7 dedicated support',
        'Custom integration',
        'SLA guarantee'
      ],
      cta: 'Contact Sales',
      popular: false
    }
  ];

  return (
    <div className="landing">
      {/* Header */}
      <header className="landing-header">
        <div className="container">
          <div className="header-content">
            <div className="logo">
              <Zap className="logo-icon" />
              <span className="logo-text">Flazu API</span>
            </div>
            <nav className="header-nav">
              <Link to="/docs" className="nav-link">Documentation</Link>
              <Link to="/pricing" className="nav-link">Pricing</Link>
              <Link to="/login" className="nav-link">Login</Link>
              <Link to="/register" className="btn btn-primary">Get Started</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <div className="badge badge-info hero-badge">
              ⚡ Pure REST API - No SDK Installation Required!
            </div>
            <h1 className="hero-title">
              Professional AI Gateway<br />
              <span className="hero-title-highlight">For Modern Developers</span>
            </h1>
            <p className="hero-description">
              Access 30+ AI models through one unified REST API. Simple HTTP requests, 
              enterprise-grade security, and optimized for performance.
            </p>
            <div className="hero-actions">
              <Link to="/register" className="btn btn-primary btn-lg">
                Start Building <ArrowRight size={20} />
              </Link>
              <Link to="/docs" className="btn btn-outline btn-lg">
                Read Docs
              </Link>
            </div>
            <div className="hero-stats">
              <div className="stat">
                <div className="stat-value">30+</div>
                <div className="stat-label">AI Models</div>
              </div>
              <div className="stat">
                <div className="stat-value">99.9%</div>
                <div className="stat-label">Uptime</div>
              </div>
              <div className="stat">
                <div className="stat-value">&lt;60s</div>
                <div className="stat-label">Response Time</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Why Choose Flazu API?</h2>
            <p className="section-description">
              Built for developers who demand reliability, performance, and simplicity.
            </p>
          </div>
          <div className="features-grid">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="feature-card">
                  <div className="feature-icon">
                    <Icon size={24} />
                  </div>
                  <h3 className="feature-title">{feature.title}</h3>
                  <p className="feature-description">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Simple, Transparent Pricing</h2>
            <p className="section-description">
              Choose the plan that fits your needs. Upgrade or downgrade anytime.
            </p>
          </div>
          <div className="pricing-grid">
            {plans.map((plan, index) => (
              <div key={index} className={`pricing-card ${plan.popular ? 'popular' : ''}`}>
                {plan.popular && <div className="popular-badge">Most Popular</div>}
                <div className="pricing-header">
                  <div className="plan-name">{plan.name}</div>
                  <div className="plan-price">
                    <span className="price">{plan.price}</span>
                    <span className="period">{plan.period}</span>
                  </div>
                </div>
                <ul className="features-list">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="feature-item">
                      <CheckCircle size={16} className="check-icon" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link 
                  to="/register" 
                  className={`btn ${plan.popular ? 'btn-primary' : 'btn-outline'} btn-block`}
                >
                  {plan.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Frequently Asked Questions</h2>
            <p className="section-description">
              Got questions? We've got answers.
            </p>
          </div>
          <div className="faq-grid">
            <div className="faq-item">
              <h4 className="faq-question">How do I get a redeem code?</h4>
              <p className="faq-answer">
                After purchasing a plan, you'll receive a redeem code via email. Use this code to activate your API key in the dashboard.
              </p>
            </div>
            <div className="faq-item">
              <h4 className="faq-question">Can I upgrade or downgrade my plan?</h4>
              <p className="faq-answer">
                Yes! You can change your plan at any time. Upgrades take effect immediately, while downgrades apply at the start of your next billing cycle.
              </p>
            </div>
            <div className="faq-item">
              <h4 className="faq-question">What happens if I exceed my rate limit?</h4>
              <p className="faq-answer">
                Requests beyond your rate limit will receive a 429 error. You can upgrade your plan for higher limits or wait for the rate limit to reset.
              </p>
            </div>
            <div className="faq-item">
              <h4 className="faq-question">What is your refund policy?</h4>
              <p className="faq-answer">
                All sales are final. We do not offer refunds. Please test with the FREE plan before upgrading to ensure our service meets your needs.
              </p>
            </div>
            <div className="faq-item">
              <h4 className="faq-question">Which AI models are supported?</h4>
              <p className="faq-answer">
                We support 30+ models including GPT-5, Claude 4.5 Sonnet, Gemini 2.5 Pro, Grok-4, DeepSeek R1, and many more. Simple REST API - no special libraries needed.
              </p>
            </div>
            <div className="faq-item">
              <h4 className="faq-question">Is there a free trial?</h4>
              <p className="faq-answer">
                Yes! Our FREE plan is available forever with 10 requests per minute. No credit card required. Upgrade anytime when you need more.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Get Started?</h2>
            <p className="cta-description">
              Join hundreds of developers building with Flazu API.
              Access 30+ cutting-edge AI models with one simple API.
            </p>
            <Link to="/register" className="btn btn-primary btn-lg">
              Create Free Account <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="logo">
                <Zap className="logo-icon" />
                <span className="logo-text">Flazu API</span>
              </div>
                          <p className="footer-description">
              Professional AI Gateway for Modern Developers. Access 30+ AI models through one unified, secure, and lightning-fast API.
            </p>
            </div>
            <div className="footer-links">
              <div className="footer-column">
                <h4>Product</h4>
                <Link to="/pricing">Pricing</Link>
                <Link to="/#features">Features</Link>
                <Link to="/docs">Documentation</Link>
                <Link to="/docs">API Reference</Link>
              </div>
              <div className="footer-column">
                <h4>Company</h4>
                <Link to="/">About Us</Link>
                <Link to="/">Blog</Link>
                <Link to="/">Contact</Link>
                <Link to="/">Support</Link>
                <Link to="/">Status</Link>
              </div>
              <div className="footer-column">
                <h4>Legal</h4>
                <Link to="/terms">Terms of Service</Link>
                <Link to="/privacy">Privacy Policy</Link>
                <Link to="/cookies">Cookie Policy</Link>
                <Link to="/dmca">DMCA</Link>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <div className="footer-copyright">
              <p>&copy; 2024 Flazu API. All rights reserved.</p>
            </div>
            <div className="footer-legal">
              <Link to="/privacy">Privacy</Link>
              <Link to="/terms">Terms</Link>
              <Link to="/cookies">Cookies</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;